import dayjs from "dayjs";
import { CustomerData, ProjectDescriptionData, ProjectDetailsDto, ProjectKeyData } from "../types/projectTypes";
import { hasDayPassed } from "./dateUtils";
import { canEditTenderingDates } from "./projectStatusUtils";

export const mapToProjectKeyData = (pd: ProjectDetailsDto | undefined): ProjectKeyData => ({
  projectName: pd?.projectName ?? "",
  jobSite: pd?.jobSite ?? "",
  tenderDate: pd?.tenderDate ? dayjs(pd.tenderDate) : undefined,
  estimatedProjectStart: pd?.estimatedProjectStart ? dayjs(pd.estimatedProjectStart) : undefined,
  estimatedProjectDuration: pd?.estimatedProjectDuration ?? "",
  estimatedProjectEuroPerBT: pd?.estimatedProjectEuroPerBT ?? 0,
  estimatedProjectEuroPerBTrange: pd?.estimatedProjectEuroPerBTrange ?? "",
  estimatedProjectBTmin: pd?.estimatedProjectBTmin ?? 0,
  estimatedProjectBTmax: pd?.estimatedProjectBTmax ?? 0,
  estimatedProjectMemberMin: pd?.estimatedProjectMemberMin ?? 0,
  estimatedProjectMemberMax: pd?.estimatedProjectMemberMax ?? 0,
  applicationStart1: pd?.applicationStart1 ? dayjs(pd.applicationStart1) : null,
  applicationEnd1: pd?.applicationEnd1 ? dayjs(pd.applicationEnd1) : undefined,
  applicationStart2: pd?.applicationStart2 ? dayjs(pd.applicationStart2) : null,
  applicationEnd2: pd?.applicationEnd2 ? dayjs(pd.applicationEnd2) : null,
  kickoff: pd?.kickoff ? dayjs(pd.kickoff) : null,
});

export const mapToCustomerData = (pd: ProjectDetailsDto | undefined): CustomerData => ({
  companyId: pd?.client?.companyId ?? -1,
  name: pd?.client?.name ?? "",
  shortDescription: pd?.client?.shortDescription ?? "",
  newCustomer: pd?.customerType === "Neukunde",
  acquisitor: pd?.acquisitor ?? "Pool",
  acquisitionMethod: pd?.acquisitionMethod ?? "Kunde",
  industry: pd?.client?.industry ?? undefined,
  contactPerson: pd?.contactPerson ?? undefined,
  street: pd?.client?.street ?? "",
  postalCode: pd?.client?.postalCode ?? "",
  city: pd?.client?.city ?? "",
  addressAdditional: pd?.client?.addressAdditional ?? "",
  url: pd?.client?.url ?? "",
  importantInformation: pd?.client?.importantInformation ?? "",
  contactDesired: pd?.client?.contactDesired ?? false,
  classified: pd?.client?.classified ?? false,
});

export const mapToProjectDescriptionData = (pd: ProjectDetailsDto | undefined): ProjectDescriptionData => ({
  situation: pd?.situation ?? "",
  peculiarities: pd?.peculiarities ?? "",
  coreCompetencies: pd?.coreCompetencies ?? [],
  requirementProfile: pd?.requirementProfile ?? "",
  referenceProjects: pd?.referenceProjects ?? "",
  notes: pd?.notes ?? "",
});

// Validate the project key data
// Checks for projectName, estimatedProjectStart, estimatedProjectDuration, estimatedProjectEuroPerBT, estimatedProjectBTmin, estimatedProjectBTmax, estimatedProjectMemberMin, estimatedProjectMemberMax and applicationEnd1
export const validateProjectKeyData = (
  projectKeyDataErrors: { [key: string]: boolean },
  projectKeyData: ProjectKeyData,
  setProjectKeyDataErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >,
  projectDetails: ProjectDetailsDto | undefined
) => {
  let hasErrors = false;
  const newErrors = { ...projectKeyDataErrors };
  if (!projectKeyData.projectName || projectKeyData.projectName.trim() === "") {
    newErrors.projectName = true;
    hasErrors = true;
  } else {
    newErrors.projectName = false;
  }
  if (
    !projectKeyData.estimatedProjectStart ||
    (projectKeyData.tenderDate && projectKeyData.estimatedProjectStart < projectKeyData.tenderDate)
  ) {
    newErrors.estimatedProjectStart = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectStart = false;
  }
  if (!projectKeyData.estimatedProjectDuration) {
    newErrors.estimatedProjectDuration = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectDuration = false;
  }
  if (!projectKeyData.estimatedProjectEuroPerBT || projectKeyData.estimatedProjectEuroPerBT < 0) {
    newErrors.estimatedProjectEuroPerBT = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectEuroPerBT = false;
  }
  if (
    !projectKeyData.estimatedProjectBTmin ||
    projectKeyData.estimatedProjectBTmin < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectBTmin)
  ) {
    newErrors.estimatedProjectBTmin = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectBTmin = false;
  }
  if (
    !projectKeyData.estimatedProjectBTmax ||
    projectKeyData.estimatedProjectBTmax < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectBTmax) ||
    (projectKeyData.estimatedProjectBTmin &&
      projectKeyData.estimatedProjectBTmax < projectKeyData.estimatedProjectBTmin)
  ) {
    newErrors.estimatedProjectBTmax = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectBTmax = false;
  }
  if (
    !projectKeyData.estimatedProjectMemberMin ||
    projectKeyData.estimatedProjectMemberMin < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectMemberMin)
  ) {
    newErrors.estimatedProjectMemberMin = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectMemberMin = false;
  }
  if (
    !projectKeyData.estimatedProjectMemberMax ||
    projectKeyData.estimatedProjectMemberMax < 0 ||
    !Number.isInteger(projectKeyData.estimatedProjectMemberMax) ||
    (projectKeyData.estimatedProjectMemberMin &&
      projectKeyData.estimatedProjectMemberMax < projectKeyData.estimatedProjectMemberMin)
  ) {
    newErrors.estimatedProjectMemberMax = true;
    hasErrors = true;
  } else {
    newErrors.estimatedProjectMemberMax = false;
  }
  const canEditApplicationDeadline = projectDetails?.status ? canEditTenderingDates(projectDetails.status) : false;

  // check if the status allows tendering dates and if the applicationEnd1 is missing or was changed to the past
  if (
    (canEditApplicationDeadline && !projectKeyData.applicationEnd1) ||
    (canEditApplicationDeadline &&
      projectKeyData.applicationEnd1 &&
      dayjs(projectDetails?.applicationEnd1).diff(projectKeyData.applicationEnd1) !== 0 &&
      hasDayPassed(projectKeyData.applicationEnd1))
  ) {
    newErrors.applicationEnd1 = true;
    hasErrors = true;
  } else {
    newErrors.applicationEnd1 = false;
  }
  // check if the status is "Bewerbung" and if the applicationStart 2 is set and if so check if the applicationEnd2 was changed or if the applicationEnd2 is null / undefined
  if (
    (projectDetails?.status === "Bewerbung" &&
      projectDetails?.applicationStart2 &&
      projectKeyData.applicationEnd2 &&
      dayjs(projectDetails?.applicationEnd2).diff(projectKeyData.applicationEnd2) !== 0 &&
      hasDayPassed(projectKeyData.applicationEnd2)) ||
    (projectDetails?.status === "Bewerbung" && projectDetails?.applicationStart2 && !projectKeyData.applicationEnd2)
  ) {
    newErrors.applicationEnd2 = true;
    hasErrors = true;
  } else {
    newErrors.applicationEnd2 = false;
  }
  setProjectKeyDataErrors(newErrors);
  return hasErrors;
};
